'use client';

import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, startOfToday, getDay } from 'date-fns';
import {
  Syringe, Calendar, User, ClipboardList, PenLine, CheckCircle2, Check,
  ArrowLeft, ArrowRight, Loader2, AlertTriangle, ShieldCheck, MapPin, FileText,
} from 'lucide-react';
import { useTheme } from '@/providers/ThemeProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useVaccineForm, useVaccineSlots, useSubmitVaccineConsent } from '@/hooks/useVaccines';
import { VaccineStepper, WizardStep } from '@/components/vaccine/VaccineStepper';
import { SignaturePad } from '@/components/vaccine/SignaturePad';
import { PhotoUpload, UploadedPhoto } from '@/components/vaccine/PhotoUpload';
import { InsuranceDisclaimer } from '@/components/vaccine/InsuranceDisclaimer';
import {
  ConsentField, groupFields, groupDetailSections, deriveContact, missingRequired,
  isFullWidthField, isVaccinesTodayField, isYesNo,
  findSignatureField, findPhotoField,
} from '@/components/vaccine/consentFields';
import { DayAvailabilityDayEnum } from '@/lib/Apis/models';
import { StepShell, DateStrip, SuccessView, isEmpty, to12h } from './BookVaccineScreen';

const WEEKDAY_BY_INDEX: DayAvailabilityDayEnum[] = [
  DayAvailabilityDayEnum.Sunday, DayAvailabilityDayEnum.Monday, DayAvailabilityDayEnum.Tuesday,
  DayAvailabilityDayEnum.Wednesday, DayAvailabilityDayEnum.Thursday, DayAvailabilityDayEnum.Friday,
  DayAvailabilityDayEnum.Saturday,
];

const DAYS_AHEAD = 30;

const SECTION_ICON: Record<string, React.ReactNode> = {
  personal: <User className="h-4 w-4" />,
  address: <MapPin className="h-4 w-4" />,
  additional: <FileText className="h-4 w-4" />,
};

type StepKey = 'schedule' | 'details' | 'screening' | 'sign' | 'confirm';

interface BookCustomVaccineScreenProps {
  /** Id of the custom vaccine (a ServiceForm with usesSharedForm = false). */
  vaccineId: string;
  /** Called when the user leaves via the back arrow on step 1 (e.g. router.back()). */
  onExit?: () => void;
}

// Booking flow for a custom vaccine: unlike the shared multi-vaccine stepper,
// the questionnaire and the schedule come from this vaccine's own form, so the
// patient sees the fields exactly as the pharmacist built them.
export function BookCustomVaccineScreen({ vaccineId, onExit }: BookCustomVaccineScreenProps) {
  const { config } = useTheme();
  const { user } = useAuth();
  const { form, isLoading: formLoading } = useVaccineForm(vaccineId);
  const submit = useSubmitVaccineConsent();

  // ── Wizard state ────────────────────────────────────────────────────────────
  const [stepIndex, setStepIndex] = useState(0);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [signature, setSignature] = useState('');
  const [certified, setCertified] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState<{ names: string[]; date?: string; time?: string } | null>(null);

  // Prefill name/phone/email from the signed-in user, once.
  React.useEffect(() => {
    if (!user || !form) return;
    setFormData((prev) => {
      if (Object.keys(prev).length) return prev;
      const seeded: Record<string, any> = {};
      const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ');
      for (const f of form.fields ?? []) {
        if (f.type === 'text' && /\bname\b/i.test(f.label) && fullName) seeded[f.key] = fullName;
        if (f.type === 'phone' && user.phoneNumber) seeded[f.key] = user.phoneNumber;
        if (f.type === 'email' && user.email) seeded[f.key] = user.email;
      }
      return seeded;
    });
  }, [user, form]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const fields = useMemo(() => form?.fields ?? [], [form]);
  const grouped = useMemo(() => groupFields(fields), [fields]);
  const detailSections = useMemo(() => groupDetailSections(grouped.details), [grouped.details]);
  const patientName = useMemo(
    () => deriveContact(fields, formData).patientName,
    [fields, formData],
  );

  const signatureField = useMemo(() => findSignatureField(fields), [fields]);
  const photoField = useMemo(() => findPhotoField(fields), [fields]);

  const appointment = form?.appointment;
  const enabledDays = useMemo(
    () => new Set((appointment?.days ?? []).filter((d) => d.enabled).map((d) => d.day)),
    [appointment],
  );
  const hasSchedule = !!appointment?.allowBooking && enabledDays.size > 0;
  const excluded = useMemo(() => new Set(appointment?.excludedDates ?? []), [appointment]);

  // Slot length comes from this form's own settings on the backend.
  const { slots, isLoading: slotsLoading, refetch: refetchSlots } =
    useVaccineSlots(hasSchedule ? vaccineId : undefined, date);

  const availableDates = useMemo(() => {
    if (!hasSchedule) return [];
    const out: Date[] = [];
    const today = startOfToday();
    for (let i = 0; i < DAYS_AHEAD; i++) {
      const d = addDays(today, i);
      const iso = format(d, 'yyyy-MM-dd');
      if (enabledDays.has(WEEKDAY_BY_INDEX[getDay(d)]) && !excluded.has(iso)) out.push(d);
    }
    return out;
  }, [hasSchedule, enabledDays, excluded]);

  // ── Dynamic steps ──────────────────────────────────────────────────────────────
  const steps: (WizardStep & { key: StepKey })[] = useMemo(() => {
    const s: (WizardStep & { key: StepKey })[] = [];
    if (hasSchedule) s.push({ key: 'schedule', label: 'Time', icon: <Calendar className="h-5 w-5" /> });
    if (grouped.details.length) s.push({ key: 'details', label: 'Details', icon: <User className="h-5 w-5" /> });
    if (grouped.screening.length) s.push({ key: 'screening', label: 'Health', icon: <ClipboardList className="h-5 w-5" /> });
    s.push({ key: 'sign', label: 'Sign', icon: <PenLine className="h-5 w-5" /> });
    s.push({ key: 'confirm', label: 'Confirm', icon: <CheckCircle2 className="h-5 w-5" /> });
    return s;
  }, [hasSchedule, grouped.details.length, grouped.screening.length]);

  const current = steps[Math.min(stepIndex, steps.length - 1)]?.key;

  // ── Step validity ────────────────────────────────────────────────────────────
  const stepValid = (key: StepKey): boolean => {
    switch (key) {
      case 'schedule': return !!date && !!time;
      case 'details': return !missingRequired(grouped.details, formData);
      case 'screening': return !missingRequired(grouped.screening, formData);
      case 'sign':
        return certified
          && (!signatureField?.required || !!signature)
          && (!photoField?.required || photos.length > 0);
      case 'confirm': return true;
      default: return true;
    }
  };

  const stepErrorMessage = (key: StepKey): string => {
    switch (key) {
      case 'schedule': return !date ? 'Please pick a date for your visit.' : 'Please choose an available time.';
      case 'sign':
        if (photoField?.required && photos.length === 0) return 'Please upload the required photo.';
        if (signatureField?.required && !signature) return 'Please add your signature.';
        return 'Please confirm the certification below to continue.';
      default: return 'Please fill in the required fields highlighted below.';
    }
  };

  const setField = (key: string, value: any) => setFormData((p) => ({ ...p, [key]: value }));

  const goNext = () => {
    if (!stepValid(current)) {
      setShowErrors(true);
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setShowErrors(false);
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => {
    setShowErrors(false);
    if (stepIndex === 0) { onExit?.(); return; }
    setStepIndex((i) => i - 1);
  };

  const handleSubmit = async () => {
    if (!form) return;
    setSubmitError('');
    const contact = deriveContact(fields, formData);
    const userFullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
    try {
      // Auto-fill any "vaccine(s) to be given today" field with this vaccine.
      const autoFilled: Record<string, any> = {};
      for (const f of fields) {
        if (isVaccinesTodayField(f)) autoFilled[f.key] = form.name;
      }

      await submit.mutateAsync({
        serviceForm: vaccineId,
        patientName: contact.patientName || userFullName || 'Guest',
        patientEmail: contact.patientEmail || user?.email,
        patientPhone: contact.patientPhone,
        formData: {
          ...formData,
          ...autoFilled,
          // Mirror the signature/photos under both the form's own field keys and
          // the canonical keys the dashboard reads ('signature' / 'photos').
          ...(signature ? { signature, ...(signatureField ? { [signatureField.key]: signature } : {}) } : {}),
          ...(photos.length
            ? { photos: photos.map((p) => p.dataUrl), ...(photoField ? { [photoField.key]: photos.map((p) => p.dataUrl) } : {}) }
            : {}),
          certified: true,
        },
        ...(hasSchedule && date && time ? { appointmentDate: date, appointmentTime: time } : {}),
      });
      setDone({ names: [form.name], date, time });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setSubmitError('That time was just booked. Please choose another slot.');
        setTime('');
        refetchSlots();
        const scheduleIdx = steps.findIndex((s) => s.key === 'schedule');
        if (scheduleIdx >= 0) setStepIndex(scheduleIdx);
      } else {
        setSubmitError('Something went wrong submitting your form. Please try again.');
      }
    }
  };

  // ── Loading / missing ──────────────────────────────────────────────────────────
  if (formLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-hprimary-500" />
      </div>
    );
  }

  if (!form || form.isActive === false) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
          <Syringe className="h-7 w-7 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Vaccine not available</h2>
        <p className="mt-1 text-sm text-gray-500">{config.storeName} isn&rsquo;t offering this vaccination online right now.</p>
      </div>
    );
  }

  if (done) return <SuccessView done={done} storeName={config.storeName} onExit={onExit} />;

  // ── Layout ──────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:pt-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          {form.description || `Book your ${form.name} visit`} · at {config.storeName}
        </p>
      </header>

      <div className="mb-5">
        <InsuranceDisclaimer />
      </div>

      <div className="mb-8">
        <VaccineStepper steps={steps} currentIndex={stepIndex} onStepClick={(i) => i < stepIndex && setStepIndex(i)} />
      </div>

      {showErrors && !stepValid(current) && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {stepErrorMessage(current)}
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.2 }}
        >
          {current === 'schedule' && (
            <StepShell title="Pick a time" subtitle="Choose a slot for your visit.">
              <DateStrip dates={availableDates} value={date} onChange={(d) => { setDate(d); setTime(''); }} />
              {date && (
                <div className="mt-5">
                  <p className="mb-2 text-sm font-medium text-gray-700">Available times</p>
                  {slotsLoading ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-gray-400">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading times…
                    </div>
                  ) : slots.filter((s) => !s.isBooked).length === 0 ? (
                    <p className="py-6 text-sm text-gray-500">No open times this day. Try another date.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          disabled={s.isBooked}
                          onClick={() => setTime(s.time)}
                          className={`rounded-xl border py-2.5 text-sm font-medium transition-all
                            ${s.isBooked ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 line-through'
                              : time === s.time ? 'border-hsecondary-500 bg-hsecondary-500 text-white shadow-sm'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-hsecondary-400'}`}
                        >
                          {to12h(s.time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </StepShell>
          )}

          {current === 'details' && (
            <StepShell title="Your details" subtitle="So the pharmacist can prepare your visit.">
              <div className="space-y-7">
                {detailSections.map((section) => (
                  <div key={section.key} className="space-y-4">
                    <div className="flex items-center gap-2.5">
                      <span className="text-hsecondary-600">{SECTION_ICON[section.key]}</span>
                      <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                      <span className="h-px flex-1 bg-gray-100" />
                    </div>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                      {section.fields.map((f) => {
                        const err = showErrors && f.required && isEmpty(formData[f.key]);
                        return (
                          <div key={f.key} className={isFullWidthField(f) ? 'sm:col-span-2' : ''}>
                            <ConsentField
                              field={f}
                              value={formData[f.key]}
                              onChange={(v) => setField(f.key, v)}
                              invalid={err}
                            />
                            {err && <p className="mt-1.5 text-xs text-red-500">This field is required.</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </StepShell>
          )}

          {current === 'screening' && (
            <StepShell title="Health screening" subtitle="A few quick questions to confirm it’s safe to vaccinate you today.">
              <div className="space-y-3">
                {grouped.screening.map((f, i) => {
                  const err = showErrors && f.required && isEmpty(formData[f.key]);
                  if (isYesNo(f)) {
                    const val = String(formData[f.key] ?? '').toLowerCase();
                    return (
                      <div
                        key={f.key}
                        className={`rounded-2xl border p-4 transition-colors
                          ${err ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-white'}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm leading-relaxed text-gray-800">
                            <span className="mr-1.5 font-semibold text-gray-300">{i + 1}.</span>
                            {f.label}
                            {f.required && <span className="text-red-500"> *</span>}
                          </p>
                          <div className="flex shrink-0 rounded-full bg-gray-100 p-0.5 sm:ml-4">
                            {['Yes', 'No'].map((opt) => {
                              const selected = val === opt.toLowerCase();
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => setField(f.key, opt.toLowerCase())}
                                  className={`min-w-[3.5rem] rounded-full px-4 py-1.5 text-sm font-medium transition-all
                                    ${selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {err && <p className="mt-2.5 text-xs text-red-500">Please select an answer.</p>}
                      </div>
                    );
                  }
                  return (
                    <div key={f.key} className={`rounded-2xl border p-4 ${err ? 'border-red-300' : 'border-gray-200 bg-white'}`}>
                      <ConsentField
                        field={f}
                        value={formData[f.key]}
                        onChange={(v) => setField(f.key, v)}
                        invalid={err}
                      />
                      {err && <p className="mt-2.5 text-xs text-red-500">Please make a selection.</p>}
                    </div>
                  );
                })}
              </div>
            </StepShell>
          )}

          {current === 'sign' && (
            <StepShell title="Review & sign" subtitle="Confirm the information you provided is accurate.">
              <div className="space-y-5">
                {photoField && (
                  <div>
                    <p className="mb-1.5 text-sm font-medium text-gray-700">
                      {photoField.label || 'Upload a photo'}
                      {photoField.required && <span className="ml-0.5 text-red-500">*</span>}
                    </p>
                    <PhotoUpload photos={photos} onChange={setPhotos} hint="e.g. insurance card or ID." />
                  </div>
                )}
                {signatureField && (
                  <div>
                    <p className="mb-1.5 text-sm font-medium text-gray-700">
                      Signature
                      {signatureField.required && <span className="ml-0.5 text-red-500">*</span>}
                    </p>
                    <SignaturePad value={signature} onChange={setSignature} />
                  </div>
                )}
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${showErrors && !certified ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${certified ? 'border-hprimary-500 bg-hprimary-500 text-white' : 'border-gray-300 bg-white'}`}>
                    {certified && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <input type="checkbox" className="sr-only" checked={certified} onChange={(e) => setCertified(e.target.checked)} />
                  <span className="text-sm text-gray-700">
                    I certify that the information I&rsquo;ve provided is true and complete to the best of my knowledge.
                  </span>
                </label>
              </div>
            </StepShell>
          )}

          {current === 'confirm' && (
            <StepShell title="Confirm your booking" subtitle="A quick review before you submit.">
              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                {hasSchedule && date && time ? (
                  <div className="flex items-center gap-3.5 border-b border-gray-100 bg-hsecondary-50 px-5 py-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-hsecondary-500 text-white shadow-sm">
                      <Calendar className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{format(new Date(`${date}T00:00:00`), 'EEEE, MMMM d')}</p>
                      <p className="text-sm text-gray-500">{to12h(time)} · {config.storeName}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3.5 border-b border-gray-100 bg-hsecondary-50 px-5 py-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-hsecondary-500 text-white shadow-sm">
                      <Syringe className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">Walk-in visit</p>
                      <p className="text-sm text-gray-500">Visit {config.storeName} any time we&rsquo;re open</p>
                    </div>
                  </div>
                )}

                <div className="px-5 py-4">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-400">Vaccine</p>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-hsecondary-50 text-hsecondary-600">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="truncate text-sm text-gray-800">{form.name}</span>
                  </div>
                </div>
              </div>

              <InsuranceDisclaimer className="mt-4" />

              <div className="mt-4 space-y-2.5">
                {patientName && (
                  <div className="flex items-center gap-2.5 text-sm text-gray-500">
                    <User className="h-4 w-4 shrink-0 text-gray-400" />
                    Booking for <span className="font-medium text-gray-700">{patientName}</span>
                  </div>
                )}
              </div>

              {submitError && (
                <p className="mt-4 rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{submitError}</p>
              )}
            </StepShell>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Sticky footer */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-100 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {current === 'confirm' ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submit.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-hprimary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-hprimary-700 disabled:opacity-60"
            >
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submit.isPending ? 'Submitting…' : 'Confirm booking'}
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="inline-flex items-center gap-2 rounded-full bg-hprimary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-hprimary-700"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
